import { ReactNode, memo, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/utils/cn";

const JobChatContent = memo(({ children }: { children: ReactNode }) => {
	return children;
});

JobChatContent.displayName = "JobChatContent";

interface JobChatPortalProps {
	children: ReactNode;
	isOpen: boolean;
	targetId?: string;
	onToggle?: (isOpen: boolean) => void;
}

export const JobChatPortal: React.FC<JobChatPortalProps> = ({
	children,
	isOpen,
	targetId = "job-chat-portal",
}) => {
	const [target, setTarget] = useState<HTMLElement | null>(null);

	useEffect(() => {
		const portalTarget = document.getElementById(targetId);
		setTarget(portalTarget);
	}, [targetId]);

	// Animate portal target width with will-change hint
	useEffect(() => {
		if (!target) return;
		target.style.willChange = "width";
		target.style.transition = "width 250ms cubic-bezier(0.4, 0, 0.2, 1)";
		target.style.width = isOpen ? "400px" : "0px";
		target.style.overflow = "hidden";
		// Width 0 and opacity 0 still leave the composer in the tab order.
		target.inert = !isOpen;

		// Remove will-change after transition to free GPU memory
		const timer = setTimeout(() => {
			target.style.willChange = "auto";
		}, 300);
		return () => clearTimeout(timer);
	}, [isOpen, target]);

	const content = (
		<div
			className={cn(
				"h-full w-[400px] transition-opacity duration-250 ease-out",
				isOpen ? "opacity-100" : "opacity-0"
			)}
			aria-hidden={!isOpen}
		>
			<JobChatContent>{children}</JobChatContent>
		</div>
	);

	return target ? createPortal(content, target) : null;
};
